"use client";

import { Box, Button as ChakraButton, Flex } from "@chakra-ui/react";
import { Text } from "@/components/ui";
import {
  formatTermsMemberNavLabel,
  formatTermsMemberNavMobileLabel,
  type TermsMemberNavItem,
} from "./terms-member-nav-config";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";

interface TermsMemberNavButtonProps {
  item: TermsMemberNavItem;
  isActive: boolean;
  onClick: () => void;
  compact?: boolean;
  /** Two waivers: equal-width segmented segments like Balance tabs. */
  equalWidth?: boolean;
  /** Mobile chips: dot instead of "(Pending)" in label. */
  showPendingDot?: boolean;
  "data-testid"?: string;
}

export const TermsMemberNavButton = ({
  item,
  isActive,
  onClick,
  compact = false,
  equalWidth = false,
  showPendingDot = false,
  "data-testid": dataTestId,
}: TermsMemberNavButtonProps) => {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();

  const label = compact
    ? formatTermsMemberNavMobileLabel(item)
    : formatTermsMemberNavLabel(item);

  const pendingDot =
    showPendingDot && item.isPending ? (
      <Box
        w="6px"
        h="6px"
        borderRadius="full"
        bg="#F59E0B"
        flexShrink={0}
        aria-hidden
      />
    ) : null;

  const labelNode = compact ? (
    <Flex
      align="center"
      justify={equalWidth ? "center" : "flex-start"}
      gap={1.5}
      minW={0}
      maxW={equalWidth ? "100%" : "200px"}
    >
      <Text
        fontSize="sm"
        fontWeight={isActive ? 600 : 500}
        lineHeight="1.25"
        textAlign={equalWidth ? "center" : "left"}
        color={isActive ? theme.accent.primary : theme.text.secondary}
        noOfLines={equalWidth ? 2 : 1}
        minW={0}
      >
        {label}
      </Text>
      {pendingDot}
    </Flex>
  ) : (
    label
  );

  return (
    <ChakraButton
      variant="ghost"
      role="tab"
      aria-selected={isActive}
      justifyContent={compact ? "center" : "flex-start"}
      w={equalWidth ? undefined : compact ? "auto" : "100%"}
      flex={equalWidth ? "1 1 0" : undefined}
      minW={equalWidth ? 0 : undefined}
      flexShrink={compact && !equalWidth ? 0 : undefined}
      h={compact ? "auto" : "auto"}
      minH={compact ? "40px" : "44px"}
      py={compact ? 2.5 : 2}
      px={compact ? 3 : 3}
      fontWeight={compact ? undefined : isActive ? "600" : "500"}
      fontSize={compact ? undefined : "sm"}
      whiteSpace={compact ? "normal" : "normal"}
      textAlign={compact ? "center" : "left"}
      lineHeight="1.4"
      color={compact ? undefined : isActive ? theme.accent.primary : theme.text.secondary}
      bg={isActive ? theme.accent.primaryBg : "transparent"}
      borderWidth="1px"
      borderColor={isActive ? theme.accent.primaryBorder : "transparent"}
      borderRadius="8px"
      onClick={onClick}
      data-testid={dataTestId ?? `terms-waiver-${item.key}-tab`}
      transition="all 0.18s ease-out"
      sx={{
        "@media (hover: hover)": {
          "&:hover": {
            bg: isActive ? theme.accent.primaryBg : interactive.neutral.bg,
          },
        },
      }}
    >
      {labelNode}
    </ChakraButton>
  );
};
